// SingleListingPage.jsx


import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import useFetchListings from "../../hooks/useFetchListings";
import useFirebaseSubmissions from "../../hooks/useFirebaseSubmissions";
import useRealtimeAvailability from "../../hooks/useRealtimeAvailability";
import Header from "../header/Header";
import QuestionnaireForm from "../../forms/QuestionnaireForm";
import ViewingRequestForm from "./ViewingRequestForm";
import { styles } from "./SingleListingPageStyles";
import { useAuth } from "../../hooks/useAuth";
const BACKEND_URL =
    (process.env.NODE_ENV === "development"
      ? "http://localhost:4000"
      : "https://ai-realtor-2ktt.onrender.com");
/* -------------------------------------------------------------------------- */
/*                               Image Gallery                                */
/* -------------------------------------------------------------------------- */
const ImageGallery = ({ images = [], title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (images.length === 0) {
    return (
      <Box sx={{ textAlign: "center", padding: "1rem" }}>
        <Typography variant="body1" color="textSecondary">
          No images available
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        mb: 4,
      }}
    >
      <Paper
        elevation={3}
        sx={{ width: "100%", maxWidth: 600, borderRadius: 2, overflow: "hidden" }}
      >
        <img
          src={images[currentIndex]}
          alt={`${title} – ${currentIndex + 1}`}
          loading="lazy"
          style={{ width: "100%", height: "auto" }}
        />
      </Paper>
      <Typography variant="body2" sx={{ mt: 1 }}>
        Image {currentIndex + 1} of {images.length}
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        {images.map((img, index) => (
          <img
            key={index}
            src={img}
            alt={`${title} – thumbnail ${index + 1}`}
            loading="lazy"
            style={{
              width: 60,
              height: 60,
              objectFit: "cover",
              margin: "0 5px",
              cursor: "pointer",
              border: currentIndex === index ? "2px solid #81c241" : "none",
            }}
            onClick={() => setCurrentIndex(index)}
          />
        ))}
      </Box>
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/*                           Single Listing Component                         */
/* -------------------------------------------------------------------------- */
const SingleListingPage = () => {
  const { id } = useParams();
  const { singleListing: listing, loading } = useFetchListings(id);
  const { submitViewingRequest } = useFirebaseSubmissions();
  const { getAvailableTimesForProperty, getUserOnboarding } = useRealtimeAvailability();
  const [step, setStep] = useState(1);
  const [weeklyTimes, setWeeklyTimes] = useState([]);
  const [specificDates, setSpecificDates] = useState([]);
  const { currentUser } = useAuth();
  const [questionnaire, setQuestionnaire] = useState({
    moveInDate: "",
    animals: "",
    dog: "",
    dogCount: "",
    cat: "",
    catCount: "",
    other: "",
    otherCount: "",
    otherDescription: "",
    esaCount: "",
    rentSource: "",
    bankruptcy: "",
    judgment: "",
    referralSource: "",
    additionalNotes: "",
  });
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [onboardingData, setOnboardingData] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  /* ------------------------------ Memo helpers ----------------------------- */
  const canonicalUrl = useMemo(() => `${window.location.origin}${location.pathname}`, [location.pathname]);

  // Helper to check if a slot overlaps with any busy block
  const isSlotFree = (slot, busyBlocks) => {
    const slotStart = new Date(slot.startTime).getTime();
    const slotEnd = new Date(slot.endTime).getTime();
    return !busyBlocks.some((b) => {
      const bStart = new Date(b.start).getTime();
      const bEnd = new Date(b.end).getTime();
      return slotStart < bEnd && slotEnd > bStart;
    });
  };

  const structuredData = useMemo(() => {
    if (!listing) return null;
    return {
      "@context": "https://schema.org",
      "@type": "RealEstateListing",
      name: listing.title,
      url: canonicalUrl,
      description: listing.description?.slice(0, 180),
      datePosted: listing.createdAt || new Date().toISOString(),
      address: {
        "@type": "PostalAddress",
        streetAddress: listing.address,
        addressLocality: listing.city,
        addressRegion: listing.state,
        postalCode: listing.zipCode,
        addressCountry: "US",
      },
      image: listing.images,
      offers: {
        "@type": "Offer",
        price: listing.rent,
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
      },
    };
  }, [listing, canonicalUrl]);

  /* --------------------------- Availability logic -------------------------- */
  const formatAvailability = (availability) => {
    if (availability.date) {
      const [y, m, d] = availability.date.split("-");
      const date = new Date(+y, +m - 1, +d).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      const start = new Date(availability.startTime).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
      const end = new Date(availability.endTime).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
      return `Specific Date: ${date}, from ${start} to ${end}`;
    }
    if (availability.day) {
      const start = new Date(availability.startTime).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
      const end = new Date(availability.endTime).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
      return `Weekly (${availability.day}): ${start} – ${end}`;
    }
    return "Invalid availability data";
  };

  /* ----------------------------- Fetch times ----------------------------- */
  useEffect(() => {
    if (step !== 2) return;
    (async () => {
      try {
        const times = await getAvailableTimesForProperty(id);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Fetch agent's busy blocks from Google Calendar
        let busyBlocks = [];
        try {
          const timeMin = new Date().toISOString();
          const timeMax = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(); 
          const gcalRes = await fetch(`${BACKEND_URL}/google-calendar/busy`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              uid: listing.ownerId,   
              timeMin,
              timeMax,
            }),
          });
          const gcalData = await gcalRes.json();
          busyBlocks = gcalData.busy ?? [];
        } catch (gcalErr) {
          console.warn("[GCal] Could not fetch busy blocks, showing all slots:", gcalErr);
        }

        setWeeklyTimes(
          times
            .filter((t) => t.day)
            .filter((t) => isSlotFree(t, busyBlocks)) 
            .map((t) => ({ ...t, displayText: formatAvailability(t) }))
        );

        setSpecificDates(
          times
            .filter((t) => {
              if (!t.date) return false;
              const [y, m, d] = t.date.split("-");
              return new Date(+y, +m - 1, +d) >= today;
            })
            .filter((t) => isSlotFree(t, busyBlocks)) 
            .map((t) => ({ ...t, displayText: formatAvailability(t) }))
        );
      } catch (err) {
        console.error("Error fetching available times", err);
      }
    })();
  }, [step, id, getAvailableTimesForProperty, listing?.ownerId]);


  /* ------------------------------ Onboarding ------------------------------- */
  useEffect(() => {
    if (!currentUser) return;
    (async () => {
      try {
        const data = await getUserOnboarding();
        setOnboardingData(data ?? {});
      } catch (err) {
        console.error("Error fetching onboarding", err);
      }
    })();
  }, [currentUser, getUserOnboarding]);

  /* --------------------- Questionnaire change / submit -------------------- */
  const handleQuestionnaireChange = (e) =>
    setQuestionnaire((q) => ({ ...q, [e.target.name]: e.target.value }));

  const handleQuestionnaireSubmit = (e) => {
    e.preventDefault();
    const required = ["moveInDate", "animals", "rentSource"];
    const missing = required.filter((f) => !questionnaire[f]);
    if (!currentUser) {
      navigate("/login", { state: { from: location } });
      return;
    }
    if (missing.length) return alert(`Fill required fields: ${missing.join(", ")}`);
    setStep(2);
  };

  /* --------------------------- Viewing request ---------------------------- */
  const handleViewingSubmit = async ({
  name, email, phone, selectedDateId, weeklyDate,
}) => {
  if (!currentUser) {
    navigate("/login", { state: { from: location } });
    return;
  }
  if (!name || !email || !selectedDateId)
    return alert("Please fill in Name, Email, and Date.");

  const selected = [...weeklyTimes, ...specificDates].find((t) => t.id === selectedDateId);
  if (!selected) return alert("Invalid availability selected");

  const data = { name, email, phone };
  let startISO, endISO;

  if (selected.day) {
    if (!weeklyDate) return alert("Select a date for the weekly slot");
    const startDT = new Date(weeklyDate);
    startDT.setHours(new Date(selected.startTime).getHours(), new Date(selected.startTime).getMinutes());
    const endDT = new Date(weeklyDate);
    endDT.setHours(new Date(selected.endTime).getHours(), new Date(selected.endTime).getMinutes());
    startISO = startDT.toISOString();
    endISO = endDT.toISOString();
    data.dateDetails = {
      type: "Weekly",
      displayText: `${formatAvailability(selected)} on ${weeklyDate}`,
      startTime: startISO,
      endTime: endISO,
    };
  } else {
    startISO = selected.startTime;
    endISO = selected.endTime;
    data.dateDetails = {
      type: "Specific Date",
      displayText: selected.displayText,
      startTime: startISO,
      endTime: endISO,
    };
  }

  setLoadingSubmit(true);
  try {
    await submitViewingRequest(id, data, questionnaire);

    // Register event on agent's Google Calendar (non-fatal if not connected)
    try {
      await fetch(`${BACKEND_URL}/google-calendar/create-event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uid: listing.ownerId,
          summary: `Viewing Request – ${listing.address}, ${listing.city}`,
          description: `Tenant: ${email} | Phone: ${phone || "N/A"}`,
          startISO,
          endISO,
          attendeeEmail: email,
        }),
      });
    } catch (gcalErr) {
      console.warn("[GCal] create-event on submit failed (non-fatal):", gcalErr);
    }

    navigate("/user-dashboard");
  } catch (err) {
    console.error(err);
    alert("Failed to submit viewing request. Try again.");
  } finally {
    setLoadingSubmit(false);
  }
};


  /* ---------------------------------------------------------------------- */
  /*                                RENDER                                 */
  /* ---------------------------------------------------------------------- */

  if (loading) {
    return (
      <>
        <Header />
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress />
          <Typography variant="body1" mt={2}>
            Loading listing details...
          </Typography>
        </Box>
      </>
    );
  }

  if (!listing) {
    return (
      <>
        <Header />
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Typography variant="h6" color="error">
            Listing not found.
          </Typography>
        </Box>
      </>
    );
  }

  /* -------------------------- Helmet meta tags --------------------------- */
  const metaTitle = `${listing.title} | ${listing.city} ${listing.state} Rental`; // adjust brand name if needed
  const metaDescription = listing.description?.slice(0, 155) ?? `${listing.title} for rent in ${listing.city}, ${listing.state}.`; // SEO-friendly description

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        {/* Open Graph */}
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={listing.images?.[0]} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={listing.images?.[0]} />
        {/* JSON‑LD structured data */}
        {structuredData && (
          <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        )}
      </Helmet>

      <Header />
      <main>
        <Box
          component="section"
          sx={{
            ...styles.container,
            py: 4,
            maxWidth: 900,
            mx: "auto",
          }}
        >
          <Typography variant="h1" component="h2" textAlign="center" mb={2}>
            {listing.title || listing.address}
          </Typography>

          {/* --------------------------- Image Gallery --------------------------- */}
          <ImageGallery images={listing.images} title={listing.title} />

          {/* ------------------------------ Details ------------------------------ */}
          <Box mb={4} textAlign="center">
            <Typography variant="h2" component="h3" mb={1}>
              ${Number(listing.rent).toLocaleString()} / month
            </Typography>
            <Typography variant="subtitle1" mb={1}>
              Location: {listing.city}, {listing.state}
            </Typography>
            <Typography variant="subtitle1" mb={1}>
              Deposit: ${Number(listing.deposit).toLocaleString()}
            </Typography>
            {listing.applicationFee && (
              <Typography variant="subtitle1" mb={1}>
                Application Fee: ${Number(listing.applicationFee).toLocaleString()}
              </Typography>
            )}
            {listing.description && (
              <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                {listing.description}
              </Typography>
            )}
          </Box>

          {/* ----------------------------- Forms ----------------------------- */}
          <Typography variant="h3" component="h4" textAlign="center" mb={2}>
            Step {step} of 2: {step === 1 ? "Pre‑Application Questionnaire" : "Request a Viewing"}
          </Typography>

          {step === 1 && (
            <Paper sx={{ p: 4, mb: 4 }} elevation={3} component="section">
              <Typography variant="h4" component="h5" mb={2}>
                Pre‑Application Questionnaire
              </Typography>
              <QuestionnaireForm
                questionnaire={questionnaire}
                handleQuestionnaireChange={handleQuestionnaireChange}
                handleQuestionnaireSubmit={handleQuestionnaireSubmit}
                loadingSubmit={loadingSubmit}
              />
            </Paper>
          )}

          {step === 2 && (
            <Paper sx={{ p: 4 }} elevation={3} component="section">
              <Typography variant="h4" component="h5" mb={2}>
                Request a Viewing
              </Typography>
              <ViewingRequestForm
                weeklyTimes={weeklyTimes}
                specificDates={specificDates}
                onSubmit={handleViewingSubmit}
                loadingSubmit={loadingSubmit}
                defaultName={onboardingData?.name ?? ""}
                defaultEmail={currentUser?.email ?? ""}
              />
            </Paper>
          )}
        </Box>
      </main>
    </>
  );
};

export default SingleListingPage;
